import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sistema.notaria4otavalo.com.ec';

const normalizeAdminFlags = (userData) => ({
  ...userData,
  is_admin: Boolean(userData?.is_admin || userData?.is_staff || userData?.is_superuser),
});

// Global interceptor: limpia sesión ante 401/403 para evitar bucles
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response && (error.response.status === 401 || error.response.status === 403)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return Promise.reject(error);
  }
);

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleAuthError = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setLoading(false);
    navigate('/login');
  }, [navigate]);

  const checkNotario = useCallback(
    async (userId, token) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/users/notario/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { user_id: userId },
        });
        return response.data.length > 0;
      } catch (error) {
        console.error('Error checking notario status:', error);
        if (error.response && error.response.status === 401) {
          handleAuthError();
        }
        return false;
      }
    },
    [handleAuthError]
  );

  const checkContador = useCallback(
    async (userId, token) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/users/contador/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { user_id: userId },
        });
        return response.data.length > 0;
      } catch (error) {
        console.error('Error checking contador status:', error);
        if (error.response && error.response.status === 401) {
          handleAuthError();
        }
        return false;
      }
    },
    [handleAuthError]
  );

  const login = useCallback(
    async (token) => {
      try {
        localStorage.setItem('token', token);
        const response = await axios.get(`${API_BASE_URL}/api/users/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const userData = normalizeAdminFlags(response.data);

        const isNotario = await checkNotario(userData.id, token);
        const isContador = await checkContador(userData.id, token);

        const enhancedUser = {
          ...userData,
          is_notario: isNotario,
          is_contador: isContador,
        };

        setUser(enhancedUser);
        setLoading(false);
      } catch (error) {
        console.error('Error during login:', error);
        handleAuthError();
      }
    },
    [checkContador, checkNotario, handleAuthError]
  );

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/users/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const userData = normalizeAdminFlags(response.data);

        const isNotario = await checkNotario(userData.id, token);
        const isContador = await checkContador(userData.id, token);

        const enhancedUser = {
          ...userData,
          is_notario: isNotario,
          is_contador: isContador,
        };

        setUser(enhancedUser);
      } catch (error) {
        console.error('Error fetching user data:', error);
        handleAuthError();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [checkContador, checkNotario, handleAuthError]);

  return <AuthContext.Provider value={{ user, loading, login }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
